import { Injectable, Injector, Type } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { RMS_PULLDOWN_DATA } from '../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';
import { RmsPulldownRef } from '../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RmsPulldownConfig } from '../custom-modules/rms-module/rms-pulldown/rms-pulldown.config';
import { RmsPulldownContainerComponent } from '../custom-modules/rms-module/rms-pulldown/rms-pulldown-container.component';

@Injectable({ providedIn: 'root' })
export class RmsPulldownService {
  constructor(
    private readonly overlay: Overlay,
    private readonly injector: Injector
  ) { }

  open<T, D = any, R = any>(
    component: Type<T>,
    config: RmsPulldownConfig<D> = {}
  ): RmsPulldownRef<R> {

    const overlayRef = this.createOverlay(config);

    let ref!: RmsPulldownRef<R>;
    ref = new RmsPulldownRef<R>(overlayRef, (result?: R) => {
      // trigger container animation close
      container.requestClose(result);
    });

    const inj = Injector.create({
      parent: this.injector,
      providers: [
        { provide: RmsPulldownRef, useValue: ref },
        { provide: RMS_PULLDOWN_DATA, useValue: config }
      ]
    });

    // 1) attach container
    const containerPortal = new ComponentPortal(RmsPulldownContainerComponent, null, inj);
    const container = overlayRef.attach(containerPortal).instance;

    // 2) attach user component into container body
    const contentInjector = Injector.create({
      parent: inj,
      providers: [
        // Your content component can inject RMS_PULLDOWN_DATA and RmsPulldownRef
        { provide: RMS_PULLDOWN_DATA, useValue: config.data }
      ]
    });

    const contentPortal = new ComponentPortal(component, null, contentInjector);
    container.attach(contentPortal);

    // close behavior
    if (config.hasBackdrop !== false && config.closeOnBackdropClick !== false) {
      overlayRef.backdropClick().subscribe(() => container.requestClose());
    }

    if (config.closeOnEscape !== false) {
      overlayRef.keydownEvents().subscribe(ev => {
        if (ev.key === 'Escape') container.requestClose();
      });
    }

    // cleanup if overlay detaches unexpectedly
    overlayRef.detachments().subscribe(() => {
      // If it detached without our animation (rare), complete the ref.
      ref._finishClose(undefined as any);
    });

    return ref;
  }

  private createOverlay<D>(config: RmsPulldownConfig<D>): OverlayRef {

    const positionStrategy = config.center
      ? this.overlay.position().global().centerHorizontally().centerVertically()
      : config.movable
        ? this.overlay.position().global().centerHorizontally().top(config.top ?? '8vh')
        : this.overlay.position().global().left('50%').top(config.top ?? '8vh');


    const overlayConfig = new OverlayConfig({
      hasBackdrop: config.hasBackdrop !== false,
      backdropClass: config.backdropClass ?? 'cdk-overlay-dark-backdrop',
      panelClass: this.asArray(config.panelClass, config.movable ? ['rms-pulldown-pane', 'rms-pulldown-movable-pane'] : ['rms-pulldown-pane']),
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      width: config.width ?? '50vw',
      maxWidth: config.maxWidth ?? '900px',
      maxHeight: config.maxHeight ?? '80vh',
      minHeight: config.minHeight,

    });

    return this.overlay.create(overlayConfig);
  }

  private asArray(val: string | string[] | undefined, defaults: string[]): string[] {
    if (!val) return defaults;
    return Array.isArray(val) ? [...defaults, ...val] : [...defaults, val];
  }
}
